import gql from "graphql-tag";
import { hasRenditions, renditionUrl } from "~/utils/cdn";
import { toCrawlableInternalPath } from "./canonicalPath";
import { colourList, framePairSlash } from "./catalogColours";

/**
 * This stays independent of the main product query. An installation without
 * the Model Variants extension can reject this optional request without
 * taking the PDP down with it.
 */
export const MODEL_VARIANTS_QUERY = gql`
  query ModelVariantsV1($productId: Int!) {
    modelVariantsV1(productId: $productId) {
      schemaVersion
      status
      statusReason
      group {
        id
        revision
        manufacturerId
        model
        label
        members {
          id
          name
          model
          manufacturerId
          url
          image
          stock
          colorCode
          frameColor
          lensColor
          productType
          size
          current
        }
      }
    }
  }
`;

export type ModelVariantLink = {
  id: string;
  name?: string;
  path: string;
  label: string;
  /** The colour part of the label, shown above the rail for the current tile. */
  colors: string;
  /** Set only when the group's sizes differ; then each tile shows it. */
  size: string;
  image: string;
  stock: boolean;
  isCurrent: boolean;
};

export type ModelVariantsState = {
  status: "ready" | "unavailable";
  groupKey: string;
  groupLabel: string;
  displayModel: string;
  current: ModelVariantLink | null;
  variants: ModelVariantLink[];
  availableAlternatives: ModelVariantLink[];
  /**
   * Set when there was no answer about the group at all: the request failed,
   * or the module did not reply "OK". An `unavailable` state without it is an
   * answer (no group, or a group this page does not show).
   */
  failed?: true;
};

type VariantMember = {
  id?: unknown;
  name?: unknown;
  model?: unknown;
  manufacturerId?: unknown;
  url?: unknown;
  image?: unknown;
  stock?: unknown;
  colorCode?: unknown;
  frameColor?: unknown;
  lensColor?: unknown;
  productType?: unknown;
  size?: unknown;
  current?: unknown;
};

type VariantGroup = {
  id?: unknown;
  revision?: unknown;
  manufacturerId?: unknown;
  model?: unknown;
  label?: unknown;
  members?: unknown;
};

type ProductIdentity = {
  id?: unknown;
  manufacturerId?: unknown;
  manufacturer?: { id?: unknown } | null;
};

const collapsed = (value: unknown) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
const normalizeId = (value: unknown) => String(value ?? "").trim();
const positiveInteger = (value: unknown): number | null => {
  const normalized = normalizeId(value);
  if (!/^\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};
const productManufacturerId = (product: ProductIdentity | null | undefined) =>
  normalizeId(product?.manufacturerId ?? product?.manufacturer?.id);

const tileImageForMember = (image: unknown): string => {
  const raw = typeof image === "string" ? image.trim() : "";
  if (!raw || (!raw.startsWith("https://") && !/^\/(?!\/)/.test(raw))) {
    return "";
  }
  return hasRenditions(raw)
    ? renditionUrl(raw, 600, { ratio: 1, format: "avif" })
    : raw;
};

type MemberFacts = {
  colors: string;
  productType: string;
  size: string;
};

// The catalog keeps several colours in one field with loose commas
// ("Havana,Brown", "Transparent,") and lens types among them ("Transparent,
// Demo Lenses"); the identity row's filter keeps only the colours.
const colourText = (value: unknown) => colourList(collapsed(value));

// "53X22X145" reads as lens, bridge and temple; other values stay as sent.
const sizeText = (value: unknown) => {
  const size = collapsed(value);
  const match = size.match(
    /^(\d+(?:\.\d+)?)\s*[x×-]\s*(\d+(?:\.\d+)?)\s*[x×-]\s*(\d+(?:\.\d+)?)\s*(?:mm)?$/i
  );
  return match ? `${match[1]}-${match[2]}-${match[3]}` : size;
};

// A few rows keep "frame / lens" in the frame field ("Black / Demo", "Black /
// Grey Polarized"). As under the H1, only the part before the slash is the
// frame's colour and no lens colour is added for such a row.
const memberFacts = (member: VariantMember): MemberFacts => {
  const frame = collapsed(member.frameColor);
  const pairSlash = framePairSlash(frame);
  const frameHoldsPair = pairSlash !== -1;
  return {
    colors: Array.from(
      new Set(
        [
          member.colorCode,
          frameHoldsPair ? frame.slice(0, pairSlash) : frame,
          frameHoldsPair ? "" : member.lensColor,
        ]
          .map(colourText)
          .filter(Boolean)
      )
    ).join(" · "),
    productType: collapsed(member.productType),
    size: sizeText(member.size),
  };
};

// A size every variant shares tells the shopper nothing, so the label only
// carries it when the group's sizes differ.
const memberLabel = (
  facts: MemberFacts,
  withSize: boolean,
  name: string
): string =>
  [facts.colors, facts.productType, withSize ? facts.size : ""]
    .filter(Boolean)
    .join(" · ") || name;

export function buildModelVariantsVariables(productId: unknown) {
  return { productId: positiveInteger(productId) };
}

export function unavailableModelVariants(groupKey = ""): ModelVariantsState {
  return {
    status: "unavailable",
    groupKey,
    groupLabel: "",
    displayModel: "",
    current: null,
    variants: [],
    availableAlternatives: [],
  };
}

// No answer about the group: a failed request, or a reply that is not "OK".
const failedModelVariants = (): ModelVariantsState => ({
  ...unavailableModelVariants(),
  failed: true,
});

/**
 * Accept only complete, extension-owned responses. We deliberately do not
 * reconstruct a group from product fields if the extension is absent or a
 * response is malformed: persisted backend membership is the source of truth.
 */
export function resolveModelVariants(
  currentProductId: unknown,
  data: unknown,
  currentProduct?: ProductIdentity | null
): ModelVariantsState {
  const currentId = normalizeId(currentProductId);
  const payload = (data as { modelVariantsV1?: unknown })?.modelVariantsV1 as
    | { schemaVersion?: unknown; status?: unknown; group?: VariantGroup | null }
    | undefined;
  const group = payload?.group;
  const requestedManufacturerId = productManufacturerId(currentProduct);

  if (!currentId || payload?.schemaVersion !== 1 || payload?.status !== "OK") {
    return failedModelVariants();
  }
  if (
    !group ||
    !normalizeId(group.id) ||
    positiveInteger(group.revision) === null ||
    !normalizeId(group.manufacturerId) ||
    (requestedManufacturerId &&
      normalizeId(group.manufacturerId) !== requestedManufacturerId) ||
    !Array.isArray(group.members)
  ) {
    return unavailableModelVariants();
  }

  const groupManufacturerId = normalizeId(group.manufacturerId);
  const seen = new Set<string>();
  const validMembers: Array<
    Omit<ModelVariantLink, "label" | "colors" | "size"> & {
      facts: MemberFacts;
      sourceName: string;
    }
  > = [];
  let currentCount = 0;

  for (const rawMember of group.members as VariantMember[]) {
    const id = normalizeId(rawMember?.id);
    const isCurrent = rawMember?.current === true;
    if (
      !id ||
      seen.has(id) ||
      normalizeId(rawMember?.manufacturerId) !== groupManufacturerId
    ) {
      return unavailableModelVariants(normalizeId(group.id));
    }
    seen.add(id);
    if (isCurrent) currentCount++;

    const path = toCrawlableInternalPath(rawMember?.url);
    const facts = memberFacts(rawMember);
    const sourceName = collapsed(rawMember?.name);
    // Current is a non-linking tile; every alternative needs a safe internal
    // destination before it can be displayed as a crawlable link.
    if ((!isCurrent && !path) || !memberLabel(facts, true, sourceName)) {
      return unavailableModelVariants(normalizeId(group.id));
    }
    validMembers.push({
      id,
      path,
      image: tileImageForMember(rawMember?.image),
      stock: rawMember?.stock === true,
      isCurrent,
      facts,
      sourceName,
    });
  }

  const current = validMembers.find((member) => member.isCurrent) || null;
  if (
    validMembers.length < 2 ||
    currentCount !== 1 ||
    !current ||
    current.id !== currentId
  ) {
    return unavailableModelVariants(normalizeId(group.id));
  }

  const sizesDiffer =
    new Set(validMembers.map((member) => member.facts.size).filter(Boolean))
      .size > 1;
  const labelled = validMembers.map(({ facts, sourceName, ...member }) => ({
    ...member,
    name: sourceName,
    label: memberLabel(facts, sizesDiffer, sourceName),
    colors: facts.colors,
    size: sizesDiffer ? facts.size : "",
  }));

  // Colour/type (and size where it differs) are the primary label. If two
  // SKUs share those facts, add their catalog names so a tooltip and no-image
  // tile remain distinct.
  const labelCounts = labelled.reduce((counts, member) => {
    counts.set(member.label, (counts.get(member.label) || 0) + 1);
    return counts;
  }, new Map<string, number>());
  const variants = labelled
    .map((member) => ({
      ...member,
      label:
        (labelCounts.get(member.label) || 0) > 1 && member.name
          ? `${member.label} · ${member.name}`
          : member.label,
    }))
    .sort((left, right) => Number(right.stock) - Number(left.stock));

  return {
    status: "ready",
    groupKey: normalizeId(group.id),
    groupLabel: collapsed(group.label) || collapsed(group.model),
    displayModel: collapsed(group.model),
    current: variants.find((member) => member.isCurrent) || null,
    variants,
    availableAlternatives: variants.filter(
      (member) => !member.isCurrent && member.stock
    ),
  };
}

type ModelVariantsFetch = (options: {
  query: typeof MODEL_VARIANTS_QUERY;
  variables: { productId: number | null };
}) => Promise<unknown> | unknown;

export function createModelVariantsFetch(
  apolloClient: any
): ModelVariantsFetch {
  return async (options) => {
    if (typeof apolloClient?.query !== "function") return null;
    const result = await apolloClient.query({
      query: options.query,
      variables: options.variables,
      // Do not reuse Apollo state after an admin changes a durable group.
      fetchPolicy: "network-only",
      errorPolicy: "none",
    });
    return result?.data ?? null;
  };
}

/**
 * The rail goes before Add to cart only where choosing a variant changes what
 * is bought: the group's sizes differ (each tile then shows its size) or the
 * current variant is sold out. Other groups offer colours of the same size and
 * sit under the purchase block (owner decision 2026-09-29).
 */
export function modelVariantsBeforePurchase(
  state: ModelVariantsState | null | undefined
): boolean {
  return (
    state?.status === "ready" &&
    (state.current?.stock === false ||
      (Array.isArray(state.variants) &&
        state.variants.some((variant) => !!variant.size)))
  );
}

// Browser-only memo of variant answers by product id. The pointer or touch
// that warms a product (prefetchProduct) starts its variant request too, and
// the PDP reuses it on the click, so Add to cart does not wait for it. An
// answer is reused for at most 15 s (the bound the SSR query cache uses for
// this query by default; browser requests are not cached on the server). Only
// the module's own "OK" answer counts, and "no group" is one. A failed or
// empty request, or a reply whose status is not "OK" (the module's
// "UNAVAILABLE"), is never kept as an answer: a page that opens after it asks
// again, while warming leaves that product alone for the same 15 s instead of
// asking on every hover or touch. A request still in flight when the page
// opens is the page's own request too, whatever it then answers.
const MODEL_VARIANTS_REUSE_MS = 15_000;
const MODEL_VARIANTS_MEMO_MAX = 64;
type ModelVariantsMemoEntry = {
  created: number;
  request: Promise<unknown>;
  /** Set once a usable answer has arrived. */
  data?: unknown;
  failed?: boolean;
};
const modelVariantsMemo = new Map<number, ModelVariantsMemoEntry>();

const usableAnswer = (data: unknown) => {
  const reply = (
    data as {
      modelVariantsV1?: { schemaVersion?: unknown; status?: unknown } | null;
    } | null
  )?.modelVariantsV1;
  return reply?.schemaVersion === 1 && reply?.status === "OK";
};

const freshMemoEntry = (productId: number) => {
  const entry = modelVariantsMemo.get(productId);
  if (!entry) return null;
  if (Date.now() - entry.created < MODEL_VARIANTS_REUSE_MS) return entry;
  modelVariantsMemo.delete(productId);
  return null;
};

/**
 * The variant answer for a product: a fresh or in-flight one from the memo,
 * or a new request. `warm` marks a prefetch on intent, which does not ask
 * again after a recent failure; the page always does. On the server every call
 * asks again (no shared memo between SSR requests).
 */
export function requestModelVariants(
  fetchData: ModelVariantsFetch,
  productId: unknown,
  options: { warm?: boolean } = {}
): Promise<unknown> {
  const variables = buildModelVariantsVariables(productId);
  const id = variables.productId;
  if (id === null) return Promise.resolve(null);
  const ask = () =>
    Promise.resolve().then(() =>
      fetchData({ query: MODEL_VARIANTS_QUERY, variables })
    );
  if (typeof window === "undefined") return ask();

  const fresh = freshMemoEntry(id);
  if (fresh && (!fresh.failed || options.warm)) return fresh.request;
  const entry: ModelVariantsMemoEntry = {
    created: Date.now(),
    request: ask(),
  };
  modelVariantsMemo.set(id, entry);
  while (modelVariantsMemo.size > MODEL_VARIANTS_MEMO_MAX) {
    const oldest = modelVariantsMemo.keys().next().value;
    if (typeof oldest === "undefined") break;
    modelVariantsMemo.delete(oldest);
  }
  entry.request.then(
    (data) => {
      if (usableAnswer(data)) entry.data = data;
      else entry.failed = true;
    },
    () => {
      entry.failed = true;
    }
  );
  return entry.request;
}

/** A fresh answer already in the memo for this product, or undefined. */
function peekModelVariants(productId: unknown): unknown {
  const id = buildModelVariantsVariables(productId).productId;
  if (id === null || typeof window === "undefined") return undefined;
  return freshMemoEntry(id)?.data;
}

/**
 * The product with its fresh variant answer resolved in, so a page that shows
 * it straight away (a warmed click, a cached product) also shows its rail and
 * Add to cart at once. Without a fresh answer the product is returned as is,
 * and so is a product that does not name its brand: a catalog card's preview
 * cannot be checked against the group's brand, so it waits for the page.
 */
export function withFreshModelVariants<T extends ProductIdentity>(
  product: T
): T | (T & { modelVariants: ModelVariantsState }) {
  if (!productManufacturerId(product)) return product;
  const data = peekModelVariants(product?.id);
  if (data === undefined) return product;
  return {
    ...product,
    modelVariants: resolveModelVariants(product?.id, data, product),
  };
}

/**
 * The fetch the PDP hands to loadModelVariants on a client-side navigation:
 * it starts (or reuses) this product's variant request right away, alongside
 * the product request, and fetches any other id normally.
 */
export function prefetchModelVariants(
  fetchData: ModelVariantsFetch,
  productId: unknown
): ModelVariantsFetch {
  const variables = buildModelVariantsVariables(productId);
  if (variables.productId === null) return fetchData;
  const pending = requestModelVariants(fetchData, productId);
  // A navigation that moves on never reads it; its failure is not an error.
  pending.catch(() => undefined);
  return (options) =>
    options.variables.productId === variables.productId
      ? pending
      : fetchData(options);
}

export async function loadModelVariants(
  fetchData: ModelVariantsFetch,
  product: ProductIdentity | null | undefined
): Promise<ModelVariantsState | null> {
  const variables = buildModelVariantsVariables(product?.id);
  if (variables.productId === null) return null;

  try {
    const data = await fetchData({ query: MODEL_VARIANTS_QUERY, variables });
    return data
      ? resolveModelVariants(product?.id, data, product)
      : failedModelVariants();
  } catch {
    return failedModelVariants();
  }
}
